import storageManager from './storageManager.js'
import { BADGES } from '../data/badges.js'

const PROFILE_KEY = 'profile'

// Points awarded per action
const PTS = {
  challenge_easy:   10,
  challenge_medium: 20,
  challenge_hard:   30,
  memory:            5,
  photo:             2,   // per photo, max 3 per memory
  trip_completed:   25,
  spot_submitted:   15,
  spot_approved:    50,
}

async function loadProfile() {
  try {
    await storageManager.initialize()
    const db = storageManager.db
    return new Promise((resolve) => {
      const tx = db.transaction('userProfile', 'readonly')
      const req = tx.objectStore('userProfile').get(PROFILE_KEY)
      req.onsuccess = () => resolve(req.result || null)
      req.onerror = () => resolve(null)
    })
  } catch { return null }
}

async function saveProfile(profile) {
  try {
    await storageManager.initialize()
    const db = storageManager.db
    return new Promise((resolve) => {
      const tx = db.transaction('userProfile', 'readwrite')
      tx.objectStore('userProfile').put(profile, PROFILE_KEY)
      tx.oncomplete = () => resolve(true)
      tx.onerror = () => resolve(false)
    })
  } catch { return false }
}

async function loadAllTrips() {
  try {
    await storageManager.initialize()
    const db = storageManager.db
    return new Promise((resolve) => {
      const tx = db.transaction('trips', 'readonly')
      const req = tx.objectStore('trips').getAll()
      req.onsuccess = () => resolve(req.result || [])
      req.onerror = () => resolve([])
    })
  } catch { return [] }
}

function computeStats(trips, spotSubmissions = []) {
  const stats = {
    totalPoints: 0,
    tripsTotal: 0,
    citiesVisited: [],
    countriesVisited: [],
    challengesCompleted: 0,
    memoriesWritten: 0,
    photosUploaded: 0,
    spotsSubmitted: spotSubmissions.length,
    spotsApproved: spotSubmissions.filter(s => s.status === 'approved').length,
  }

  const citySet = new Set()
  const countrySet = new Set()

  for (const trip of trips) {
    const data = trip.tripData || trip
    const notes = data.notes || []

    // Destination
    const city = data.destination?.city || data.dates?.destination || ''
    const country = data.destination?.country || ''
    if (city) citySet.add(city)
    if (country) countrySet.add(country)

    let tripHasContent = false

    for (const note of notes) {
      if (note.isChallenge && note.challengeData?.submitted) {
        const diff = (note.challengeData.difficulty || 'easy').toLowerCase()
        stats.totalPoints += PTS[`challenge_${diff}`] || PTS.challenge_easy
        stats.challengesCompleted++
        tripHasContent = true
      } else if (!note.isActivity && !note.isChallenge) {
        stats.totalPoints += PTS.memory
        stats.memoriesWritten++
        tripHasContent = true
        const photoCount = Math.min(note.photos?.length || 0, 3)
        stats.totalPoints += photoCount * PTS.photo
        stats.photosUploaded += photoCount
      }
    }

    if (tripHasContent) {
      stats.totalPoints += PTS.trip_completed
      stats.tripsTotal++
    }
  }

  // Points for community spots
  stats.totalPoints += spotSubmissions.length * PTS.spot_submitted
  const approvedSpots = spotSubmissions.filter(s => s.status === 'approved').length
  stats.totalPoints += approvedSpots * PTS.spot_approved

  stats.citiesVisited = Array.from(citySet)
  stats.countriesVisited = Array.from(countrySet)
  return stats
}

function evaluateBadges(stats, existingBadges = []) {
  const earned = new Set(existingBadges.map(b => b.id))
  const newBadges = []

  for (const badge of BADGES) {
    if (!earned.has(badge.id) && badge.condition(stats)) {
      const entry = { id: badge.id, earnedAt: new Date().toISOString() }
      newBadges.push(entry)
      earned.add(badge.id)
    }
  }

  return newBadges
}

// Main export — call this after any save that could affect stats.
// Returns { profile, newBadges } so the caller can show a toast/notification.
export async function aggregateProfile() {
  try {
    const [trips, existing] = await Promise.all([loadAllTrips(), loadProfile()])
    const spotSubmissions = existing?.spotSubmissions || []
    const stats = computeStats(trips, spotSubmissions)
    const existingBadges = existing?.badges || []
    const newBadges = evaluateBadges(stats, existingBadges)

    const profile = {
      updatedAt: new Date().toISOString(),
      stats,
      badges: [...existingBadges, ...newBadges],
      spotSubmissions,
    }

    await saveProfile(profile)
    window.__cachedProfile = profile
    return { profile, newBadges }
  } catch (e) {
    console.error('[profileManager] aggregation failed:', e)
    return { profile: null, newBadges: [] }
  }
}

// Fast read — returns cached profile or loads from IndexedDB
export async function getProfile() {
  if (window.__cachedProfile) return window.__cachedProfile
  const profile = await loadProfile()
  if (profile) window.__cachedProfile = profile
  return profile
}

// Record a new spot submission locally and re-aggregate.
export async function recordSpotSubmission(submissionId, country, city, name) {
  try {
    const existing = await loadProfile()
    const submissions = existing?.spotSubmissions || []
    submissions.push({ submissionId, country, city, name, status: 'pending', submittedAt: new Date().toISOString() })
    const updated = { ...(existing || {}), spotSubmissions: submissions }
    await saveProfile(updated)
    window.__cachedProfile = null
    return aggregateProfile()
  } catch { return aggregateProfile() }
}

// Called on app startup — checks server for challenge + spot approval updates.
// Updates local IndexedDB if anything changed, then returns true if any change happened.
export async function checkPendingStatuses() {
  try {
    const [trips, profile] = await Promise.all([loadAllTrips(), loadProfile()])
    if (!trips.length && !(profile?.spotSubmissions?.length)) return false

    // Collect pending challenge submission IDs across all trips
    const challengeIds = []
    for (const trip of trips) {
      const notes = (trip.tripData || trip).notes || []
      for (const note of notes) {
        if (note.isChallenge && note.challengeData?.submissionId
            && !note.challengeData?.approved && !note.challengeData?.rejected) {
          challengeIds.push(note.challengeData.submissionId)
        }
      }
    }

    // Collect pending spot submission IDs
    const spotIds = (profile?.spotSubmissions || [])
      .filter(s => s.status === 'pending')
      .map(s => s.submissionId)

    if (!challengeIds.length && !spotIds.length) return false

    const resp = await fetch('./api/challenges/check-status.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challengeIds, spotIds }),
    })
    if (!resp.ok) return false
    const { data } = await resp.json()

    let anyChange = false

    // Update challenge statuses in trip notes
    if (data?.challenges && Object.keys(data.challenges).length) {
      await storageManager.initialize()
      const db = storageManager.db
      for (const trip of trips) {
        const tripData = trip.tripData || trip
        const notes = tripData.notes || []
        let tripChanged = false
        for (const note of notes) {
          const srv = data.challenges[note.challengeData?.submissionId]
          if (srv && srv.status !== 'pending') {
            note.challengeData.approved        = srv.status === 'approved'
            note.challengeData.rejected        = srv.status === 'rejected'
            note.challengeData.rejectionReason = srv.rejectionReason || null
            tripChanged = true
            anyChange = true
          }
        }
        if (tripChanged) {
          const tx = db.transaction('trips', 'readwrite')
          tx.objectStore('trips').put(trip)
          await new Promise(r => { tx.oncomplete = r; tx.onerror = r })
          window.__cachedTripData = null
        }
      }
    }

    // Update spot submission statuses in profile
    if (data?.spots && Object.keys(data.spots).length) {
      const subs = profile?.spotSubmissions || []
      let spotsChanged = false
      for (const sub of subs) {
        const srv = data.spots[sub.submissionId]
        if (srv && srv.status !== sub.status) {
          sub.status    = srv.status
          sub.reviewedAt = srv.reviewedAt
          spotsChanged = true
          anyChange = true
        }
      }
      if (spotsChanged) {
        const updated = { ...(profile || {}), spotSubmissions: subs }
        await saveProfile(updated)
        window.__cachedProfile = null
      }
    }

    return anyChange
  } catch (e) {
    console.warn('[profileManager] checkPendingStatuses failed:', e)
    return false
  }
}
